import { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { SlidersHorizontal, RotateCcw, Info } from 'lucide-react';

// Feature flag control panel (Radly platform). Reads the declared registry +
// resolved state from /admin/features and writes per-feature overrides. The
// resolved state already accounts for entitlement ceiling + dependencies.
export default function Features() {
  const [data, setData] = useState(null); // { registry, platform, flags, entitlements, resolved }
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/admin/features');
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    if (!data?.registry) return [];
    const byGroup = {};
    for (const f of data.registry) {
      (byGroup[f.group] = byGroup[f.group] || []).push(f);
    }
    return Object.entries(byGroup);
  }, [data]);

  const setFlag = async (id, value) => {
    setSavingId(id);
    try {
      const res = await api.put('/admin/features', { flags: { [id]: value } });
      // Server returns merged flags + freshly resolved active set.
      setData((d) => ({ ...d, flags: res.data.flags, resolved: res.data.resolved }));
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
      await load();
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  const flags = data?.flags || {};
  const resolved = data?.resolved || {};

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <SlidersHorizontal className="w-6 h-6 text-amber-500" />
        <h1 className="text-2xl font-bold text-white">Features</h1>
      </div>
      <p className="text-gray-400 mb-6">
        Turn capabilities on or off for this instance. Changes apply within a minute (clients refresh on next launch).
        The <span className="text-gray-200">Active</span> badge reflects the effective state after plan limits and dependencies.
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-700 text-red-300 text-sm">{error}</div>
      )}

      {data?.entitlements && (
        <div className="mb-6 p-3 rounded-lg bg-gray-800 border border-gray-700 text-sm text-gray-300 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
          <span>Plan tier: <span className="text-white font-medium">{data.entitlements.tier || 'custom'}</span>. Features above your plan are locked off regardless of this switch.</span>
        </div>
      )}

      <div className="space-y-6">
        {groups.map(([group, items]) => (
          <div key={group} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700 bg-gray-800/60">
              <h2 className="text-sm font-semibold text-gray-200 uppercase tracking-wide">{group}</h2>
            </div>
            <div className="divide-y divide-gray-700/60">
              {items.map((f) => {
                const isActive = resolved[f.id] !== false;
                const explicit = Object.prototype.hasOwnProperty.call(flags, f.id);
                const desiredOn = explicit ? flags[f.id] !== false : f.default;
                const blocked = desiredOn && !isActive; // wanted on but plan/deps block it
                return (
                  <div key={f.id} className="flex items-center gap-4 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{f.label}</span>
                        <span className="text-[11px] text-gray-500 font-mono">{f.id}</span>
                        {f.tier && f.tier !== 'core' && (
                          <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">{f.tier}</span>
                        )}
                      </div>
                      {f.description && <div className="text-sm text-gray-400 mt-0.5">{f.description}</div>}
                      {f.dependsOn?.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">Requires: {f.dependsOn.join(', ')}</div>
                      )}
                      {blocked && (
                        <div className="text-xs text-amber-400 mt-1">On, but blocked by plan or a disabled dependency.</div>
                      )}
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-green-900/40 text-green-300' : 'bg-gray-700 text-gray-400'}`}>
                        {isActive ? 'Active' : 'Off'}
                      </span>

                      {explicit ? (
                        <button
                          onClick={() => setFlag(f.id, null)}
                          disabled={savingId === f.id}
                          title="Reset to default"
                          className="text-gray-500 hover:text-gray-300"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-[11px] text-gray-600 w-4 text-center" title="Using default">·</span>
                      )}

                      <button
                        role="switch"
                        aria-checked={desiredOn}
                        disabled={savingId === f.id}
                        onClick={() => setFlag(f.id, !desiredOn)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${desiredOn ? 'bg-amber-500' : 'bg-gray-600'} ${savingId === f.id ? 'opacity-50' : ''}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${desiredOn ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
